import { MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS } from '~/settings/components/ConfigurationsPage/helpers/utils';
import { parseConfigValue, formatConfigValue } from '~/settings/utils/configValueParser';

import type { ConfigRecord, ConfigField } from '~/settings/components/ConfigurationsPage/types';

/**
 * Converts flattened form data to API payload: for shrink.min_size_bytes and datafy.min_size_bytes,
 * halves the displayed value before sending to the API. For percentage fields, strips "%" so the
 * backend receives a plain number (e.g. "90" not "90%").
 */
export const buildApiPayload = (
  flattenedData: Record<string, string | number>,
  configRecord?: ConfigRecord | null
): Record<string, string | number> => {
  const payload: Record<string, string | number> = { ...flattenedData };
  for (const key of MIN_SIZE_BYTES_DOUBLE_DISPLAY_FIELDS) {
    const value = payload[key];
    if (value !== undefined && value !== null && value !== '') {
      const parsed = parseConfigValue(String(value), 'size');
      payload[key] = formatConfigValue(parsed.value / 2, parsed.unit, 'size');
    }
  }
  configRecord?.fields.forEach((field) => {
    if (field.type === 'percentage' && payload[field.name] !== undefined) {
      payload[field.name] = String(payload[field.name]).replace(/%/g, '').trim();
    }
  });
  return payload;
};

export const buildSavedFields = (
  configRecord: ConfigRecord,
  flattenedData: Record<string, string | number>
): Record<string, string | number> => {
  const savedFields: Record<string, string | number> = {};
  configRecord.fields.forEach((field) => {
    if (flattenedData[field.name] !== undefined) {
      savedFields[field.name] = flattenedData[field.name];
    } else {
      savedFields[field.name] = field.currentValue;
    }
  });
  return savedFields;
};

export const buildFlattenedData = (
  configRecord: ConfigRecord,
  getValues: (name: string) => string | number | undefined
): Record<string, string | number> => {
  const flattenedData: Record<string, string | number> = {};
  configRecord.fields.forEach((field) => {
    const value = getValues(field.name);
    if (value !== undefined && value !== null && value !== '') {
      flattenedData[field.name] = value;
    }
  });
  return flattenedData;
};

export const buildOriginalValues = (configRecord: ConfigRecord): Record<string, string> => {
  const originalValues: Record<string, string> = {};
  configRecord.fields.forEach((field) => {
    originalValues[field.name] = field.currentValue;
  });
  return originalValues;
};

export const createDisplayField = (
  field: ConfigField,
  formFields: Record<string, string | number | undefined>
): ConfigField => {
  const currentValue =
    formFields[field.name] !== undefined ? String(formFields[field.name]) : field.currentValue;

  return {
    ...field,
    currentValue,
    originalValue: field.currentValue,
  };
};
